/* eslint-disable camelcase */
import { liveProviderDetail, parseLiveProvider } from './liveProvider'

import type { SpeakerMap } from '@/api/generated'

export type TranscriptSource = 'mic' | 'system' | 'refined'

type TranscriptSpeaker = {
  id: number
  source: TranscriptSource
  name?: string | null
}

type TranscriptSpeechEntry = {
  liveProvider?: unknown
  id: string
  timestamp: string
  text: string
  speaker: TranscriptSpeaker
  latched?: boolean | null
  startRecordingMs?: unknown
  endRecordingMs?: unknown
}

type TranscriptSimpleMarkerEntry = {
  id: string
  timestamp: string
  marker: 'paused' | 'resumed' | 'dictated'
}

type TranscriptLabeledMarkerEntry = {
  id: string
  timestamp: string
  marker: 'audio_event' | 'sleep_gap' | 'offline_gap'
  label: string
}

export type TranscriptEntry = TranscriptSpeechEntry | TranscriptSimpleMarkerEntry | TranscriptLabeledMarkerEntry
export type LaneId = 'live' | 'refined' | 'observations' | 'original' | 'cluster'

export type SpeakerIdentity = {
  key: string
  colorKey: string
  label: string
  name?: string | null
  speakerId?: number
  captureSource?: TranscriptSource
  timelineLabel?: string
}

export type AttributionRow = {
  providerDetail?: string
  entry_id: string | null
  start_ms: number
  end_ms: number
  speaker: SpeakerIdentity | null
  observationState?: 'silence'
  text: string | null
  method?: string
  channel?: 'mic' | 'system'
  // Per-row, never on the identity: the label has to stay stable per colorKey.
  detail?: string | null
  // The row disagrees with its lane's comparison baseline on this interval.
  changed?: boolean
}

export type AttributionSpan = Omit<AttributionRow, 'entry_id' | 'text'>

export type TranscriptParseResult = {
  entries: TranscriptEntry[]
  invalidLineCount: number
}

export type ObservationData = {
  participants: Map<string, string | null>
  platform: string | null
  os: string | null
  speaking: { t_ms: number; speaking: string[] | null }[]
  invalidLineCount: number
  usable: boolean
}

export type SpeakerAssignmentSource = {
  isResolved: boolean
  mechanism: 'user' | 'dom' | 'mic' | 'llm'
  name: string | null
  origin: string | null
  personId: string | null
  platform: string | null
}

export type SpeakerMapping = {
  assignmentSources: SpeakerAssignmentSource[]
  colorKey: string
  name: string | null
  origin: string | null
  personId: string | null
  platform: string | null
  speakerId: number
}

export type ConsolidatedSpeakerMapping = {
  colorKey: string
  name: string | null
  origin: string | null
  personId: string | null
  platform: string | null
  resolvedSources: SpeakerAssignmentSource[]
  speakerIds: number[]
}

export type UnassignedPerson = {
  name: string
  origin: string
  personId: string
  platform: string | null
}

export type RawTranscriptData = {
  invalidTokenCount: number
  rows: AttributionRow[]
  usable: boolean
}

const FINAL_TAIL_MS = 2_000
const TRANSCRIPT_SOURCES = new Set(['mic', 'system', 'refined'])
export const isTranscriptSource = (value: unknown): value is TranscriptSource =>
  typeof value === 'string' && TRANSCRIPT_SOURCES.has(value)
const SIMPLE_MARKER_TYPES = new Set(['paused', 'resumed', 'dictated'])
const LABELED_MARKER_TYPES = new Set(['audio_event', 'sleep_gap', 'offline_gap'])

const LANE_LABELS: Record<LaneId, string> = {
  live: 'Live',
  refined: 'Refined',
  observations: 'AX tree',
  original: 'Original',
  cluster: 'GradientBoost',
}

export const SOURCE_LABELS: Record<TranscriptSpeaker['source'], string> = {
  mic: 'Mic',
  refined: 'Refined',
  system: 'System',
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null

const rawTokens = (value: Record<string, unknown>): unknown[] | null => {
  if (Array.isArray(value.tokens)) return value.tokens
  const transcript = asRecord(value.transcript)
  if (transcript && Array.isArray(transcript.tokens)) return transcript.tokens
  const segments = transcript && Array.isArray(transcript.segments) ? transcript.segments : value.segments
  if (!Array.isArray(segments)) return null
  return segments.flatMap((segment) => {
    const record = asRecord(segment)
    return record && Array.isArray(record.tokens) ? record.tokens : []
  })
}

const rawMs = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && !Number.isNaN(value) ? Math.round(value) : null

const clusterIdentity = (speaker: unknown, sourceLabel: string): SpeakerIdentity => {
  let clusterKey = 'unknown'
  let label = 'Unknown cluster'
  if (typeof speaker === 'number' && Number.isInteger(speaker) && speaker > 0) {
    clusterKey = `num:${speaker}`
    label = `Cluster ${speaker}`
  } else if (typeof speaker === 'string' && speaker.trim()) {
    const value = speaker.trim()
    clusterKey = `label:${value}`
    label = `Cluster ${value}`
  }
  return {
    key: `raw:cluster:${clusterKey}`,
    colorKey: `cluster:${clusterKey}`,
    label: `${sourceLabel} · ${label}`,
    timelineLabel: label,
  }
}

export function parseRawTranscript(body: string, sourceLabel = 'Soniox'): RawTranscriptData {
  let payload: Record<string, unknown> | null = null
  try {
    payload = asRecord(JSON.parse(body))
  } catch {
    return { invalidTokenCount: 0, rows: [], usable: false }
  }
  if (!payload) return { invalidTokenCount: 0, rows: [], usable: false }
  const tokens = rawTokens(payload)
  if (!tokens) return { invalidTokenCount: 0, rows: [], usable: false }

  const kept: {
    startMs: number
    explicitEndMs: number | null
    speaker: SpeakerIdentity
    text: string
  }[] = []
  let invalidTokenCount = 0
  for (const value of tokens) {
    const token = asRecord(value)
    const startMs = token ? rawMs(token.start_ms) : null
    if (!token || typeof token.text !== 'string' || startMs == null || token.is_audio_event === true) {
      if (token?.is_audio_event !== true) invalidTokenCount += 1
      continue
    }
    const explicitEndMs = rawMs(token.end_ms)
    kept.push({
      startMs,
      explicitEndMs: explicitEndMs != null && explicitEndMs >= startMs ? explicitEndMs : null,
      speaker: clusterIdentity(token.speaker, sourceLabel),
      text: token.text,
    })
  }

  kept.sort((a, b) => a.startMs - b.startMs)
  const rows = kept.map((token, index): AttributionRow => ({
    entry_id: null,
    start_ms: token.startMs,
    end_ms: token.explicitEndMs ?? Math.max(token.startMs, kept[index + 1]?.startMs ?? token.startMs),
    speaker: token.speaker,
    text: token.text,
  }))

  return { invalidTokenCount, rows, usable: true }
}

export function parseChorusTranscript(body: string): RawTranscriptData {
  let payload: Record<string, unknown> | null
  try {
    payload = asRecord(JSON.parse(body))
  } catch {
    return { invalidTokenCount: 0, rows: [], usable: false }
  }
  if (payload?.provider !== 'chorus' || !Array.isArray(payload.tokens))
    return { invalidTokenCount: 0, rows: [], usable: false }
  const rows: AttributionRow[] = []
  let invalidTokenCount = 0
  for (const value of payload.tokens) {
    const token = asRecord(value)
    const start = rawMs(token?.start_ms)
    const end = rawMs(token?.end_ms)
    const channel = token?.channel
    if (
      !token ||
      typeof token.text !== 'string' ||
      !token.text.trim() ||
      typeof token.speaker !== 'string' ||
      !token.speaker.trim() ||
      (channel !== 'mic' && channel !== 'system') ||
      start == null ||
      end == null ||
      end < start
    ) {
      invalidTokenCount += 1
      continue
    }
    const cluster = token.speaker.startsWith(`${channel}:`) ? token.speaker.slice(channel.length + 1) : token.speaker
    const colorKey = `cluster:chorus:${channel}:${cluster}`
    const label = `${channel === 'mic' ? 'Mic' : 'System'} · Cluster ${cluster}`
    rows.push({
      entry_id: null,
      start_ms: start,
      end_ms: end,
      channel,
      speaker: { key: `raw:${colorKey}`, colorKey, label: `Chorus · ${label}`, timelineLabel: label },
      text: token.text,
    })
  }
  rows.sort((a, b) => a.start_ms - b.start_ms)
  return { invalidTokenCount, rows, usable: invalidTokenCount === 0 || rows.length > 0 }
}

const isSpeaker = (value: unknown): value is TranscriptSpeaker => {
  const speaker = asRecord(value)
  return Boolean(
    speaker &&
    Number.isInteger(speaker.id) &&
    TRANSCRIPT_SOURCES.has(String(speaker.source)) &&
    (speaker.name == null || typeof speaker.name === 'string')
  )
}

const transcriptEntry = (value: unknown): TranscriptEntry | null => {
  const entry = asRecord(value)
  if (!entry || typeof entry.id !== 'string' || typeof entry.timestamp !== 'string') return null
  if (typeof entry.text === 'string' && isSpeaker(entry.speaker)) {
    if (entry.latched !== undefined && entry.latched !== null && typeof entry.latched !== 'boolean') return null
    return value as TranscriptSpeechEntry
  }
  if (typeof entry.marker !== 'string') return null
  if (SIMPLE_MARKER_TYPES.has(entry.marker)) return value as TranscriptSimpleMarkerEntry
  if (LABELED_MARKER_TYPES.has(entry.marker) && typeof entry.label === 'string') {
    return value as TranscriptLabeledMarkerEntry
  }
  return null
}

export function parseTranscriptNdjson(body: string): TranscriptParseResult {
  const entries: TranscriptEntry[] = []
  let invalidLineCount = 0
  for (const line of body.split('\n')) {
    if (!line.trim()) continue
    try {
      const entry = transcriptEntry(JSON.parse(line))
      if (entry) entries.push(entry)
      else invalidLineCount += 1
    } catch {
      invalidLineCount += 1
    }
  }
  return { entries, invalidLineCount }
}

export function parseTranscriptEntries(values: readonly unknown[]): TranscriptParseResult {
  const entries: TranscriptEntry[] = []
  let invalidLineCount = 0
  for (const value of values) {
    const entry = transcriptEntry(value)
    if (entry) entries.push(entry)
    else invalidLineCount += 1
  }
  return { entries, invalidLineCount }
}

const sanitizedName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const name = Array.from(value, (character) => {
    const code = character.charCodeAt(0)
    return code <= 31 || code === 127 ? ' ' : character
  })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200)
  return name || null
}

export function parseObservationNdjson(body: string): ObservationData {
  const participants = new Map<string, string | null>()
  const speaking: ObservationData['speaking'] = []
  let invalidLineCount = 0
  let platform: string | null = null
  let os: string | null = null
  let sawMeta = false

  for (const line of body.split('\n')) {
    if (!line.trim()) continue
    let record: Record<string, unknown> | null = null
    try {
      record = asRecord(JSON.parse(line))
    } catch {
      invalidLineCount += 1
      continue
    }
    if (!record) {
      invalidLineCount += 1
      continue
    }
    if (!sawMeta) {
      const meta = asRecord(record.meta)
      if (!meta)
        return {
          participants,
          platform,
          os,
          speaking,
          invalidLineCount: invalidLineCount + 1,
          usable: false,
        }
      platform = sanitizedName(meta.platform)
      os = sanitizedName(meta.os)
      sawMeta = true
      continue
    }

    const participant = asRecord(record.participant)
    if (participant) {
      if (typeof participant.pid === 'string') participants.set(participant.pid, sanitizedName(participant.name))
      else invalidLineCount += 1
      continue
    }

    const observation = asRecord(record.obs)
    if (observation && Object.prototype.hasOwnProperty.call(observation, 'speaking')) {
      const tMs = observation.tMs
      const speakerIds = observation.speaking
      if (
        typeof tMs === 'number' &&
        Number.isFinite(tMs) &&
        tMs >= 0 &&
        (speakerIds === null || (Array.isArray(speakerIds) && speakerIds.every((pid) => typeof pid === 'string')))
      ) {
        speaking.push({
          t_ms: Math.round(tMs),
          speaking: speakerIds as string[] | null,
        })
      } else {
        invalidLineCount += 1
      }
    }
  }

  speaking.sort((a, b) => a.t_ms - b.t_ms)
  return {
    participants,
    platform,
    os,
    speaking,
    invalidLineCount,
    usable: sawMeta,
  }
}

export function parseTranscriptTimestamp(value: string): number {
  const parts = value.split(':').map(Number)
  if (parts.some((part) => !Number.isInteger(part) || part < 0)) return 0
  if (parts.length === 2) return parts[0] * 60_000 + parts[1] * 1_000
  if (parts.length === 3) return parts[0] * 3_600_000 + parts[1] * 60_000 + parts[2] * 1_000
  return 0
}

const isSpeechEntry = (entry: TranscriptEntry): entry is TranscriptSpeechEntry => 'speaker' in entry

export const normalizedSpeakerName = (name: string): string =>
  name.normalize('NFKC').replace(/\s+/g, ' ').trim().toLocaleLowerCase()

export function identityForSpeaker(lane: LaneId, speaker: TranscriptSpeaker): SpeakerIdentity {
  // AX names can change within one diarized cluster, so they cannot define its identity.
  if (lane === 'live') {
    return {
      key: `live:${speaker.source}:${speaker.id}`,
      colorKey: `cluster:live:${speaker.source}:${speaker.id}`,
      label: `${LANE_LABELS.live} · ${SOURCE_LABELS[speaker.source]} · Speaker ${speaker.id}`,
      timelineLabel: `${SOURCE_LABELS[speaker.source]} · Speaker ${speaker.id}`,
      captureSource: speaker.source,
    }
  }
  const source = speaker.source.replace(/_/g, ' ')
  const name = speaker.name?.trim()
  const normalizedName = name ? normalizedSpeakerName(name) : null
  const isRefined = lane === 'refined'
  return {
    key: !isRefined && normalizedName ? `${lane}:name:${normalizedName}` : `${lane}:${speaker.source}:${speaker.id}`,
    colorKey: !isRefined && normalizedName ? `name:${normalizedName}` : `speaker:${speaker.id}`,
    label: name
      ? `${LANE_LABELS[lane]} · ${source} · ${name}`
      : `${LANE_LABELS[lane]} · ${source} · Speaker ${speaker.id}`,
    name,
    speakerId: isRefined ? speaker.id : undefined,
    timelineLabel: name || `Speaker ${speaker.id}`,
  }
}

// The `timestamp` label is second-granular and not a reliable start time, so a valid recorded span wins.
const isRecordingMs = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0

// A malformed span falls back to the label clock; the row is kept, not dropped.
const recordingSpan = ({ startRecordingMs: start, endRecordingMs: end }: TranscriptSpeechEntry) =>
  isRecordingMs(start) && isRecordingMs(end) && end >= start ? { start, end } : null

export function entriesToRows(entries: TranscriptEntry[], lane: LaneId): AttributionRow[] {
  const speech = entries
    .filter(isSpeechEntry)
    .map((entry, index) => {
      const span = lane === 'live' ? recordingSpan(entry) : null
      return {
        entry,
        index,
        start_ms: span?.start ?? parseTranscriptTimestamp(entry.timestamp),
        recorded_end_ms: span?.end ?? null,
      }
    })
    .sort((a, b) => a.start_ms - b.start_ms || a.index - b.index)

  // Clip the fallback within one source only, so mic and system rows are never tiled against each other.
  const endMs = new Array<number>(speech.length)
  const nextStartBySource = new Map<TranscriptSource, number>()
  for (let index = speech.length - 1; index >= 0; index -= 1) {
    const { entry, start_ms, recorded_end_ms } = speech[index]
    endMs[index] =
      recorded_end_ms ?? Math.max(start_ms, nextStartBySource.get(entry.speaker.source) ?? start_ms + FINAL_TAIL_MS)
    nextStartBySource.set(entry.speaker.source, start_ms)
  }

  const hasKnownProvider = speech.some(
    ({ entry }) => entry.speaker.source !== 'refined' && parseLiveProvider(entry.liveProvider) !== undefined
  )
  return speech.map(({ entry, start_ms }, index) => ({
    entry_id: entry.id,
    start_ms,
    end_ms: endMs[index],
    speaker: identityForSpeaker(lane, entry.speaker),
    text: entry.text,
    detail: lane === 'live' ? liveRowDetail(entry) : null,
    ...(lane === 'live' && hasKnownProvider
      ? {
          providerDetail: liveProviderDetail(entry.speaker.source === 'refined' ? undefined : entry.liveProvider),
        }
      : {}),
  }))
}

// The Live lane no longer keys on these, but the viewer exists to inspect them.
function liveRowDetail(entry: TranscriptSpeechEntry): string {
  const parts = [`Speaker ${entry.speaker.id}`]
  const name = entry.speaker.name?.trim()
  if (name) parts.push(`AX “${name}”`)
  if (entry.latched) parts.push('latched')
  return parts.join(' · ')
}

export type TranscriptStats = { spanMs: number; words: number }

export function transcriptStats(rows: AttributionRow[]): TranscriptStats {
  if (rows.length === 0) return { spanMs: 0, words: 0 }
  let start = Infinity
  let end = 0
  let words = 0
  for (const row of rows) {
    start = Math.min(start, row.start_ms)
    end = Math.max(end, row.end_ms)
    words += row.text?.split(/\s+/).filter(Boolean).length ?? 0
  }
  return { spanMs: end - start, words }
}

export function mergeConsecutiveSpeakerRows(rows: AttributionRow[]): AttributionRow[] {
  const merged: AttributionRow[] = []
  for (const row of rows) {
    const last = merged.at(-1)
    const key = row.speaker?.key ?? row.observationState ?? ''
    if (
      last &&
      (last.speaker?.key ?? last.observationState ?? '') === key &&
      last.method === row.method &&
      last.providerDetail === row.providerDetail &&
      last.end_ms >= row.start_ms
    ) {
      last.end_ms = Math.max(last.end_ms, row.end_ms)
      continue
    }
    merged.push({ ...row })
  }
  return merged
}

export function rowsToSpans(rows: AttributionRow[]): AttributionSpan[] {
  // Lanes can carry concurrent speakers (the AX tile log emits one row per speaking
  // participant per snapshot), so merge per speaker rather than against the last span.
  const spans: AttributionSpan[] = []
  const openByKey = new Map<string, AttributionSpan>()
  for (const row of rows) {
    const key = `${row.speaker?.key ?? row.observationState ?? ''}\u0000${row.method ?? ''}`
    const previous = openByKey.get(key)
    if (previous && previous.end_ms >= row.start_ms) {
      previous.end_ms = Math.max(previous.end_ms, row.end_ms)
    } else {
      const span = {
        start_ms: row.start_ms,
        end_ms: row.end_ms,
        speaker: row.speaker,
        ...(row.method ? { method: row.method } : {}),
        ...(row.observationState ? { observationState: row.observationState } : {}),
      }
      spans.push(span)
      openByKey.set(key, span)
    }
  }
  return spans
}

export function observationsToRows(data: ObservationData): AttributionRow[] {
  const rows: AttributionRow[] = []
  for (let index = 0; index < data.speaking.length; index += 1) {
    const snapshot = data.speaking[index]
    const endMs = data.speaking[index + 1]?.t_ms ?? snapshot.t_ms + FINAL_TAIL_MS
    if (snapshot.speaking === null || endMs <= snapshot.t_ms) continue
    if (snapshot.speaking.length === 0) {
      rows.push({
        entry_id: null,
        start_ms: snapshot.t_ms,
        end_ms: endMs,
        speaker: null,
        observationState: 'silence',
        text: null,
      })
      continue
    }
    for (const pid of snapshot.speaking) {
      const name = data.participants.get(pid)
      const colorKey = name ? `name:${normalizedSpeakerName(name)}` : `participant:${pid}`
      rows.push({
        entry_id: null,
        start_ms: snapshot.t_ms,
        end_ms: endMs,
        speaker: {
          key: name ? `observations:${colorKey}` : `observations:participant:${pid}`,
          colorKey,
          label: name ? `AX tree · participant · ${name}` : `AX tree · participant ${pid}`,
          name,
          timelineLabel: name || `Participant ${pid}`,
        },
        text: null,
      })
    }
  }
  return rows.sort((a, b) => a.start_ms - b.start_ms || (a.speaker?.key ?? '').localeCompare(b.speaker?.key ?? ''))
}

const ASSIGNMENT_SOURCES = ['user', 'dom', 'mic', 'llm'] as const
export const RESOLUTION_SLOTS = [...ASSIGNMENT_SOURCES, 'consensus'] as const

export function deriveSpeakerMappings(speakerMap: SpeakerMap | null | undefined): SpeakerMapping[] {
  if (!speakerMap?.assignments) return []
  const rows: Omit<SpeakerMapping, 'colorKey'>[] = []
  for (const [speakerId, assignment] of Object.entries(speakerMap.assignments)) {
    const numericId = Number(speakerId)
    if (!Number.isInteger(numericId) || numericId < 0) continue
    const resolvedMechanism = RESOLUTION_SLOTS.find((slot) => assignment[slot] != null) ?? null
    const resolvedPersonId = resolvedMechanism ? (assignment[resolvedMechanism] ?? null) : null
    const person = resolvedPersonId ? speakerMap.people?.[resolvedPersonId] : undefined
    const assignmentSources = ASSIGNMENT_SOURCES.map<SpeakerAssignmentSource>((mechanism) => {
      const sourcePersonId = assignment[mechanism] ?? null
      const sourcePerson = sourcePersonId ? speakerMap.people?.[sourcePersonId] : undefined
      return {
        isResolved: mechanism === resolvedMechanism,
        mechanism,
        name: sourcePerson?.name ?? null,
        origin: sourcePerson?.origin ?? null,
        personId: sourcePersonId,
        platform: sourcePerson?.platform ?? null,
      }
    })
    rows.push({
      assignmentSources,
      name: person?.name ?? null,
      origin: person?.origin ?? null,
      personId: person ? resolvedPersonId : null,
      platform: person?.platform ?? null,
      speakerId: numericId,
    })
  }
  rows.sort((a, b) => a.speakerId - b.speakerId)

  const firstSpeakerByPerson = new Map<string, number>()
  for (const row of rows) {
    if (row.personId && !firstSpeakerByPerson.has(row.personId)) firstSpeakerByPerson.set(row.personId, row.speakerId)
  }
  return rows.map((row) => ({
    ...row,
    colorKey: `speaker:${row.personId ? firstSpeakerByPerson.get(row.personId) : row.speakerId}`,
  }))
}

export function consolidateSpeakerMappings(mappings: SpeakerMapping[]): ConsolidatedSpeakerMapping[] {
  const consolidated: ConsolidatedSpeakerMapping[] = []
  const byPersonId = new Map<string, ConsolidatedSpeakerMapping>()
  for (const mapping of mappings) {
    const resolvedSources = mapping.assignmentSources.filter((source) => source.isResolved)
    const existing = mapping.personId ? byPersonId.get(mapping.personId) : undefined
    if (!existing) {
      const entry: ConsolidatedSpeakerMapping = {
        colorKey: mapping.colorKey,
        name: mapping.name,
        origin: mapping.origin,
        personId: mapping.personId,
        platform: mapping.platform,
        resolvedSources,
        speakerIds: [mapping.speakerId],
      }
      consolidated.push(entry)
      if (mapping.personId) byPersonId.set(mapping.personId, entry)
      continue
    }
    existing.speakerIds.push(mapping.speakerId)
    for (const source of resolvedSources) {
      if (!existing.resolvedSources.some((seen) => seen.mechanism === source.mechanism)) {
        existing.resolvedSources.push(source)
      }
    }
    existing.resolvedSources.sort(
      (a, b) => ASSIGNMENT_SOURCES.indexOf(a.mechanism) - ASSIGNMENT_SOURCES.indexOf(b.mechanism)
    )
  }
  return consolidated
}

export function deriveUnassignedPeople(
  speakerMap: SpeakerMap | null | undefined,
  mappings: SpeakerMapping[]
): UnassignedPerson[] {
  if (!speakerMap?.people) return []
  const assignedPersonIds = new Set(mappings.flatMap((mapping) => (mapping.personId == null ? [] : [mapping.personId])))
  return Object.entries(speakerMap.people)
    .filter(([personId]) => !assignedPersonIds.has(personId))
    .map(([personId, person]) => ({
      name: person.name,
      origin: person.origin,
      personId,
      platform: person.platform ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.personId.localeCompare(b.personId))
}

export function applySpeakerMappings(rows: AttributionRow[], mappings: SpeakerMapping[]): AttributionRow[] {
  if (mappings.length === 0) return rows
  const bySpeakerId = new Map(mappings.map((mapping) => [mapping.speakerId, mapping]))
  const candidatesByName = new Map<string, SpeakerMapping[]>()
  for (const mapping of mappings) {
    if (!mapping.name) continue
    const key = normalizedSpeakerName(mapping.name)
    candidatesByName.set(key, [...(candidatesByName.get(key) ?? []), mapping])
  }

  return rows.map((row) => {
    if (!row.speaker) return row
    const idMapping = row.speaker.speakerId == null ? undefined : bySpeakerId.get(row.speaker.speakerId)
    const nameMatches = row.speaker.name ? candidatesByName.get(normalizedSpeakerName(row.speaker.name)) : undefined
    const nameMapping =
      nameMatches && new Set(nameMatches.map((mapping) => mapping.personId)).size === 1 ? nameMatches[0] : undefined
    const mapping = idMapping ?? nameMapping
    if (!mapping) return row
    const labelParts = row.speaker.label.split(' · ')
    const label = mapping.name ? [...labelParts.slice(0, 2), mapping.name].join(' · ') : row.speaker.label
    return {
      ...row,
      speaker: {
        ...row.speaker,
        colorKey: mapping.colorKey,
        label,
        name: mapping.name ?? row.speaker.name,
        timelineLabel: mapping.name ?? row.speaker.timelineLabel,
      },
    }
  })
}

// Only the refine-stage slots. `consensus` falls back to `llm`, and `user` is a later edit.
const ASSIGNER_SLOTS = ['dom', 'mic'] as const

// Re-names already-mapped refined rows from the assigner's slots alone, before summary-stage LLM naming and edits.
// The inline speaker name is the served one, so an id the assigner never named must drop it rather than fall back.
export function assignerOnlyRows(
  refinedRows: AttributionRow[],
  speakerMap: SpeakerMap | null | undefined,
  mappings: SpeakerMapping[]
): AttributionRow[] {
  const colorKeyByPerson = new Map<string, string>()
  for (const mapping of mappings) {
    if (mapping.personId && !colorKeyByPerson.has(mapping.personId))
      colorKeyByPerson.set(mapping.personId, mapping.colorKey)
  }

  return refinedRows.map((row) => {
    const speaker = row.speaker
    if (speaker?.speakerId == null) return row
    const assignment = speakerMap?.assignments?.[String(speaker.speakerId)]
    const slot = ASSIGNER_SLOTS.find((candidate) => assignment?.[candidate] != null)
    const personId = slot ? (assignment?.[slot] ?? null) : null
    const name = (personId ? speakerMap?.people?.[personId]?.name?.trim() : null) || null
    const source = speaker.label.split(' · ')[1] ?? ''
    const display = name ?? `Speaker ${speaker.speakerId}`
    const servedName = speaker.name ? normalizedSpeakerName(speaker.name) : null
    const changed = servedName !== (name ? normalizedSpeakerName(name) : null)
    return {
      ...row,
      speaker: {
        key: `assigner:${source}:${speaker.speakerId}`,
        // An unnamed id keeps its refined colour, so the bar lines up with the cluster it came from.
        colorKey: name && personId ? (colorKeyByPerson.get(personId) ?? `assigner:${personId}`) : speaker.colorKey,
        label: `Assigner · ${source} · ${display}`,
        name,
        speakerId: speaker.speakerId,
        timelineLabel: display,
      },
      ...(changed ? { changed: true, detail: speaker.timelineLabel ?? speaker.label } : {}),
    }
  })
}

export function reconcileNamedSpeakerColors(
  rows: AttributionRow[],
  namedReferenceRows: AttributionRow[]
): AttributionRow[] {
  const colorsByName = new Map<string, Set<string>>()
  for (const row of namedReferenceRows) {
    if (!row.speaker?.name) continue
    const name = normalizedSpeakerName(row.speaker.name)
    const colors = colorsByName.get(name) ?? new Set<string>()
    colors.add(row.speaker.colorKey)
    colorsByName.set(name, colors)
  }

  return rows.map((row) => {
    if (!row.speaker?.name) return row
    const colors = colorsByName.get(normalizedSpeakerName(row.speaker.name))
    if (colors?.size !== 1) return row
    return {
      ...row,
      speaker: {
        ...row.speaker,
        colorKey: Array.from(colors)[0],
      },
    }
  })
}

export function filterRows(rows: AttributionRow[], from: number, to: number): AttributionRow[] {
  return rows.filter((row) => row.end_ms > from && row.start_ms < to)
}

export function artifactEndMs(lanes: { rows: AttributionRow[] }[]): number {
  let end = 0
  for (const lane of lanes) {
    for (const row of lane.rows) end = Math.max(end, row.end_ms)
  }
  return Math.max(end, 1)
}
